/* "Change this slide's design" — 8 layout gallery.
   Costs 2 credits per commit. Shows a grid of eight visual layout options
   (KPI dashboard, 3 pain-point cards, Process flow, TAM/SAM/SOM, 2×2 matrix,
   Team photo cards, Timeline/roadmap, Funnel diagram) each with a mini SVG
   preview. The founder picks one, hits Apply, and both the web preview + the
   exported .pptx re-render with that layout. */

import { useEffect, useMemo, useState } from "react";
import { PaintBrushBroad, Sparkle, X, ArrowRight, CheckCircle, ArrowCounterClockwise, TextT, Columns } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { extractErrorMessage, toastApiError } from "@/lib/apiError";
import { diffWords, diffToOldSegments, diffToNewSegments } from "@/lib/wordDiff";

const C = {
  panel: "#0A2826",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
  green: "#34D399",
};

/* The eight design templates the founder can commit to. `id` is what we send
   to the backend as `answers.layout` (must match one of the options in
   pitch_ai.py → IMPROVE_QUESTIONS). Each option ships with a tiny SVG
   thumbnail so the founder sees the shape of the layout before choosing. */
const LAYOUTS = [
  {
    id: "Numbered vertical list (bold 01/02/03 markers)",
    title: "Numbered list",
    tag: "Generic bullets · Key points",
    desc: "Bold 01/02/03 markers next to each bullet — turns a plain list into a confident, scannable stack.",
    Thumb: () => (
      <svg viewBox="0 0 100 56" className="w-full h-full">
        <rect width="100" height="56" fill="#fff" />
        <rect x="6" y="4" width="30" height="3" fill="#0A8B85" />
        {[0, 1, 2, 3].map((i) => (
          <g key={i}>
            <text x="10" y={17 + i * 10} fontSize="7" fontWeight="800" fill="#0A8B85">0{i + 1}</text>
            <rect x="22" y={14 + i * 10} width={62} height="2" fill="#333" />
            <rect x="22" y={17.5 + i * 10} width={44} height="1.5" fill="#999" />
          </g>
        ))}
      </svg>
    ),
  },
  {
    id: "Two-column bullet split",
    title: "Two-column split",
    tag: "Generic bullets · Balanced",
    desc: "Bullets balanced across two columns — perfect for slides with 4–8 points to keep the layout airy.",
    Thumb: () => (
      <svg viewBox="0 0 100 56" className="w-full h-full">
        <rect width="100" height="56" fill="#fff" />
        <rect x="6" y="4" width="30" height="3" fill="#0A8B85" />
        {[0, 1, 2, 3].map((i) => {
          const col = i % 2, row = Math.floor(i / 2);
          const x = 8 + col * 46, y = 16 + row * 14;
          return (
            <g key={i}>
              <circle cx={x} cy={y + 1} r="1.5" fill="#F59E0B" />
              <rect x={x + 4} y={y} width={36} height={1.8} fill="#333" />
              <rect x={x + 4} y={y + 4} width={30} height={1.5} fill="#999" />
              <rect x={x + 4} y={y + 8} width={26} height={1.5} fill="#999" />
            </g>
          );
        })}
      </svg>
    ),
  },
  {
    id: "Quote / callout cards (highlighted bullets)",
    title: "Callout cards",
    tag: "Generic bullets · Highlighted",
    desc: "Each bullet becomes its own highlighted card with a brand-colour left bar — great for emphasising 3–4 punchy claims.",
    Thumb: () => (
      <svg viewBox="0 0 100 56" className="w-full h-full">
        <rect width="100" height="56" fill="#fff" />
        <rect x="6" y="4" width="30" height="3" fill="#0A8B85" />
        {[0, 1, 2].map((i) => (
          <g key={i}>
            <rect x="8" y={14 + i * 12} width="86" height="9" rx="1.4" fill="#F1F5F4" />
            <rect x="8" y={14 + i * 12} width="3" height="9" fill="#0A8B85" />
            <rect x="15" y={16 + i * 12} width={60} height="1.8" fill="#333" />
            <rect x="15" y={19 + i * 12} width={40} height="1.5" fill="#999" />
          </g>
        ))}
      </svg>
    ),
  },
  {
    id: "Photo left, bullets right (mirror split)",
    title: "Photo left · text right",
    tag: "Photo · Mirror split",
    desc: "Full-bleed photo on the LEFT half, headline + bullets on the RIGHT half. The mirror of the classic photo split.",
    Thumb: () => (
      <svg viewBox="0 0 100 56" className="w-full h-full">
        <rect width="100" height="56" fill="#fff" />
        <rect x="0" y="0" width="50" height="56" fill="#0A8B85" opacity="0.12" />
        <circle cx="18" cy="16" r="5" fill="#F59E0B" opacity="0.85" />
        <path d="M 0 56 L 12 34 L 22 42 L 32 28 L 42 40 L 50 34 L 50 56 Z" fill="#0A8B85" opacity="0.55" />
        <path d="M 0 56 L 8 46 L 18 50 L 28 44 L 38 52 L 50 46 L 50 56 Z" fill="#0A8B85" opacity="0.85" />
        <rect x="54" y="10" width="42" height="4" rx="0.5" fill="#0A8B85" />
        <rect x="54" y="18" width="34" height="2" fill="#666" />
        <rect x="54" y="24" width="38" height="2" fill="#666" />
        <rect x="54" y="30" width="30" height="2" fill="#666" />
        <rect x="54" y="36" width="34" height="2" fill="#666" />
        <rect x="54" y="46" width="12" height="4" rx="2" fill="#F59E0B" />
      </svg>
    ),
  },
  {
    id: "Photo top, bullets bottom",
    title: "Photo top · bullets below",
    tag: "Photo · Horizontal split",
    desc: "Wide photo band across the top half, headline + bullets stacked below. Ideal for opening a section with a strong visual moment.",
    Thumb: () => (
      <svg viewBox="0 0 100 56" className="w-full h-full">
        <rect width="100" height="56" fill="#fff" />
        <rect x="0" y="0" width="100" height="26" fill="#0A8B85" opacity="0.12" />
        <circle cx="80" cy="10" r="4" fill="#F59E0B" opacity="0.85" />
        <path d="M 0 26 L 20 12 L 34 18 L 52 8 L 68 16 L 84 10 L 100 18 L 100 26 Z" fill="#0A8B85" opacity="0.6" />
        <path d="M 0 26 L 12 20 L 30 22 L 50 18 L 72 22 L 90 20 L 100 24 L 100 26 Z" fill="#0A8B85" opacity="0.85" />
        <rect x="6" y="30" width="34" height="3.5" rx="0.4" fill="#0A8B85" />
        <rect x="6" y="38" width="70" height="1.8" fill="#666" />
        <rect x="6" y="43" width="58" height="1.8" fill="#666" />
        <rect x="6" y="48" width="66" height="1.8" fill="#666" />
      </svg>
    ),
  },
  {
    id: "Photo hero with bullets overlay",
    title: "Photo hero + overlay",
    tag: "Photo · Full-bleed drama",
    desc: "Full-bleed photo behind a semi-transparent dark panel with bullets and headline in white. Cinematic hero moment.",
    Thumb: () => (
      <svg viewBox="0 0 100 56" className="w-full h-full">
        <rect x="0" y="0" width="100" height="56" fill="#0A8B85" opacity="0.28" />
        <circle cx="80" cy="14" r="6" fill="#F59E0B" opacity="0.9" />
        <path d="M 0 56 L 20 30 L 34 40 L 54 24 L 74 34 L 92 26 L 100 32 L 100 56 Z" fill="#0A2826" opacity="0.55" />
        <path d="M 0 56 L 14 44 L 30 48 L 50 42 L 72 48 L 92 44 L 100 48 L 100 56 Z" fill="#0A2826" opacity="0.8" />
        <rect x="6" y="12" width="52" height="34" fill="#0A2826" opacity="0.72" rx="1" />
        <rect x="10" y="16" width="32" height="3.5" rx="0.4" fill="#fff" />
        <rect x="10" y="24" width="42" height="1.6" fill="#fff" opacity="0.75" />
        <rect x="10" y="29" width="36" height="1.6" fill="#fff" opacity="0.75" />
        <rect x="10" y="34" width="40" height="1.6" fill="#fff" opacity="0.75" />
        <rect x="10" y="41" width="10" height="3" rx="1.5" fill="#F59E0B" />
      </svg>
    ),
  },
  {
    id: "Photo grid (3 tiles) with bullets",
    title: "Photo grid + bullets",
    tag: "Photos · Multi-image story",
    desc: "Bullets on the left, three stacked photo tiles on the right. Perfect when the slide needs multiple visual references.",
    Thumb: () => (
      <svg viewBox="0 0 100 56" className="w-full h-full">
        <rect width="100" height="56" fill="#fff" />
        <rect x="4" y="8" width="36" height="4" rx="0.4" fill="#0A8B85" />
        <rect x="4" y="16" width="42" height="2" fill="#666" />
        <rect x="4" y="22" width="36" height="2" fill="#666" />
        <rect x="4" y="28" width="42" height="2" fill="#666" />
        <rect x="4" y="34" width="30" height="2" fill="#666" />
        <rect x="4" y="46" width="12" height="4" rx="2" fill="#F59E0B" />
        {[0, 1, 2].map((i) => {
          const y = 4 + i * 17;
          return (
            <g key={i}>
              <rect x="55" y={y} width="41" height="14" rx="1" fill="#0A8B85" opacity={0.18 + i * 0.12} />
              <circle cx={64 + i * 3} cy={y + 5} r="2.4" fill="#F59E0B" opacity="0.85" />
              <path d={`M 55 ${y + 14} L 66 ${y + 8} L 76 ${y + 11} L 88 ${y + 6} L 96 ${y + 10} L 96 ${y + 14} Z`}
                fill="#0A2826" opacity={0.4 + i * 0.08} />
            </g>
          );
        })}
      </svg>
    ),
  },
  {
    id: "Picture only (full-bleed photo, no text)",
    title: "Picture only",
    tag: "Photo · No text",
    desc: "One full-bleed photo edge-to-edge. No headline, no bullets — just the picture. Perfect for a single strong visual moment.",
    Thumb: () => (
      <svg viewBox="0 0 100 56" className="w-full h-full">
        <rect width="100" height="56" fill="#0A8B85" opacity="0.16" />
        <circle cx="72" cy="18" r="7" fill="#F59E0B" opacity="0.9" />
        <path d="M 0 56 L 18 30 L 34 42 L 52 24 L 72 36 L 90 28 L 100 34 L 100 56 Z" fill="#0A2826" opacity="0.55" />
        <path d="M 0 56 L 14 46 L 32 50 L 52 44 L 72 50 L 92 46 L 100 50 L 100 56 Z" fill="#0A2826" opacity="0.8" />
      </svg>
    ),
  },
];

// Fast lookup by layout id. Populated from the LAYOUTS array above.
const LAYOUTS_BY_ID = Object.fromEntries(LAYOUTS.map((l) => [l.id, l]));

// Fallback order used before the backend responds. The backend
// (GET /api/pitch/layouts) is the single source of truth; this is only
// what shows on the very first paint if the fetch is still in flight.
const LOCAL_LAYOUT_ORDER = LAYOUTS.map((l) => l.id);

// Layouts that use `slide.images[0..N]` as the actual photo source in
// SlidePreviewBody. When one of these is applied to a slide that has no
// images, we offer auto-generation via Nano Banana. Note: "3-4 column
// team photo cards" is intentionally excluded — those photos come from
// the deck's `team[]` entries, not slide.images.
const PHOTO_LAYOUT_IDS = new Set([
  "Illustrated hero split (text + illustration)",
  "Photo left, bullets right (mirror split)",
  "Photo top, bullets bottom",
  "Photo hero with bullets overlay",
  "Photo grid (3 tiles) with bullets",
]);

export default function ImproveSlideDesign({
  deckId,
  slideNum,
  slideTitle,
  slideImages = [],
  extraId = null,
  onAutoPhotoGenerated,
}) {
  const [open, setOpen] = useState(false);
  const [savedPrefs, setSavedPrefs] = useState({});
  const [pick, setPick] = useState(null);        // currently-selected layout id (string)
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);       // true once applied successfully
  const [autoPhotoPrompt, setAutoPhotoPrompt] = useState(null); // {layoutId} or null
  const [autoPhotoBusy, setAutoPhotoBusy] = useState(false);
  const [premiumBusy, setPremiumBusy] = useState(false);
  // Diff preview modal — populated after a premium rewrite so the founder
  // can inspect old vs new side-by-side and revert with one click if they
  // don't like it. Reverting doesn't refund credits (the AI call already
  // happened) — the founder got the preview, that's the value.
  const [diffPreview, setDiffPreview] = useState(null);
  const [restoring, setRestoring] = useState(false);
  // Diff view mode — "inline" (word-level highlighted single pane, default)
  // or "sxs" (side-by-side plain-text panes, legacy). Persisted per-session
  // in state so founders can pick their preferred review style.
  const [diffView, setDiffView] = useState("inline");
  // Ordered list of layout ids to render, sourced from the backend. Falls
  // back to the local order until the first fetch resolves. Filtered to
  // only ids that have local visual metadata (Thumb, title, tag, desc) so
  // adding a new layout on the backend without a matching frontend
  // thumbnail doesn't render as an empty card — it just silently hides
  // until the gallery is updated. This is the single-source-of-truth link.
  const [layoutOrder, setLayoutOrder] = useState(LOCAL_LAYOUT_ORDER);
  // Compact `?extra_id=...` suffix used on every design endpoint call when
  // the founder is operating on a sub-slide rather than the parent. Empty
  // string on parent slides so the endpoints stay backward compatible.
  const extraQs = extraId ? `?extra_id=${encodeURIComponent(extraId)}` : "";

  // Fetch canonical layout list once on mount. The backend
  // (/api/pitch/layouts) returns the authoritative order + IDs; we only
  // keep the ones we know how to render locally.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/pitch/layouts");
        if (cancelled) return;
        const ids = (data?.layouts || [])
          .map((l) => l.id)
          .filter((id) => LAYOUTS_BY_ID[id]);
        if (ids.length > 0) setLayoutOrder(ids);
      } catch (_) {
        // Silent fallback — LOCAL_LAYOUT_ORDER is already in state.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch the slide's existing design_prefs so we can highlight the current
  // pick when the modal opens.
  useEffect(() => {
    if (!open) return;
    setDone(false);
    (async () => {
      try {
        const { data } = await api.get(
          `/pitch/decks/${deckId}/slide/${slideNum}/improve-questions${extraQs}`
        );
        const prefs = data.saved_prefs || {};
        setSavedPrefs(prefs);
        setPick(prefs.layout || null);
      } catch (_) {
        setSavedPrefs({});
        setPick(null);
      }
    })();
  }, [open, deckId, slideNum, extraId]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    if (!pick) return;
    setLoading(true);
    try {
      // Reuse existing endpoint — pass ONLY the layout choice (plus any
      // previously-saved prefs for other axes so we don't wipe them). Charges
      // 2 credits and persists design_prefs; we ignore the AI critique.
      const answers = { ...savedPrefs, layout: pick };
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${slideNum}/improve-suggestions${extraQs}`,
        { answers }
      );
      window.dispatchEvent(new Event("clinq:credits-changed"));
      window.dispatchEvent(new CustomEvent("clinq:design-prefs-saved", {
        detail: { deckId, slideNum, extraId, prefs: data.design_prefs || answers },
      }));
      setDone(true);
      toast.success(`Design changed · ${data.cost_credits || 2} credits used — preview updated`);
      // If this is a photo layout and the slide has no photo uploaded yet,
      // offer to auto-generate one via Nano Banana. We do NOT auto-close the
      // gallery in that case — the founder decides first.
      const isPhotoLayout = PHOTO_LAYOUT_IDS.has(pick);
      const hasNoPhoto = !(slideImages || []).some((im) => im?.url);
      if (isPhotoLayout && hasNoPhoto) {
        setAutoPhotoPrompt({ layoutId: pick });
      } else {
        // Auto-close after a short beat so the founder sees the confirmation
        setTimeout(() => setOpen(false), 900);
      }
    } catch (e) {
      const detail = extractErrorMessage(e, "Could not apply new design");
      toast.error(typeof detail === "string" ? detail : "Could not apply new design");
    } finally { setLoading(false); }
  };

  const generateAutoPhoto = async () => {
    setAutoPhotoBusy(true);
    try {
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${slideNum}/auto-photo${extraQs}`
      );
      window.dispatchEvent(new Event("clinq:credits-changed"));
      if (data?.image && onAutoPhotoGenerated) {
        onAutoPhotoGenerated(data.image);
      }
      toast.success(`Photo generated · ${data?.cost_credits || 3} credits used — you can upload your own to replace it anytime`);
      setAutoPhotoPrompt(null);
      // Close the design gallery so the founder sees the new photo in place
      setTimeout(() => setOpen(false), 400);
    } catch (e) {
      const detail = extractErrorMessage(e, "Photo generation failed. Please try again.");
      toast.error(typeof detail === "string" ? detail : "Photo generation failed. Please try again.");
    } finally {
      setAutoPhotoBusy(false);
    }
  };

  const skipAutoPhoto = () => {
    setAutoPhotoPrompt(null);
    toast("You can upload a photo in the Photos section, or come back and try auto-generate later.", { duration: 4500 });
    setTimeout(() => setOpen(false), 200);
  };

  /** Regenerate the slide's content using Claude Opus 4.5 with a prompt tuned
   *  to the currently-selected layout. Costs 3 credits (COST_AI_PREMIUM_REWRITE).
   *  Useful for legacy slides whose content doesn't fit the target layout's
   *  structured shape — e.g. bulletty Market Size content that renders empty
   *  on the TAM/SAM/SOM concentric-circles layout. */
  const premiumRewrite = async () => {
    if (!pick) return;
    setPremiumBusy(true);
    try {
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${slideNum}/premium-rewrite${extraQs}`,
        { layout: pick, extra_id: extraId || null },
        { headers: { "Idempotency-Key": crypto.randomUUID() } },
      );
      window.dispatchEvent(new Event("clinq:credits-changed"));
      // Push the new content into the live preview immediately so the
      // founder sees it re-rendered UNDER the diff modal.
      window.dispatchEvent(new CustomEvent("clinq:premium-rewrite-applied", {
        detail: { deckId, slideNum, extraId, content: data.content },
      }));
      // Open the diff modal instead of auto-closing. The founder can pick
      // "Keep new" (no-op — already saved) or "Restore original" (calls
      // the appropriate PUT endpoint to overwrite).
      setDiffPreview({
        oldContent: data.previous_content || "",
        newContent: data.content || "",
        layout: data.layout_used || pick,
      });
    } catch (e) {
      toastApiError(e, "Premium rewrite failed. Please try again.", {
        context: { deck_id: deckId, slide_num: slideNum, extra_id: extraId || null, action: "premium_rewrite", layout: pick },
      });
    } finally { setPremiumBusy(false); }
  };

  /** Revert the slide back to the pre-rewrite content. Uses the parent PUT
   *  endpoint for regular slides, or the extras PUT endpoint for sub-slides.
   *  Credits are NOT refunded — the founder still got the preview value. */
  const restoreOriginal = async () => {
    if (!diffPreview) return;
    setRestoring(true);
    try {
      const url = extraId
        ? `/pitch/decks/${deckId}/slide/${slideNum}/extras/${extraId}`
        : `/pitch/decks/${deckId}/slide/${slideNum}`;
      await api.put(url, { content: diffPreview.oldContent });
      // Push the original content back into the live preview so the
      // founder sees the revert land immediately.
      window.dispatchEvent(new CustomEvent("clinq:premium-rewrite-applied", {
        detail: { deckId, slideNum, extraId, content: diffPreview.oldContent },
      }));
      toast.success("Restored — the original content is back on your slide.");
      setDiffPreview(null);
      setTimeout(() => setOpen(false), 500);
    } catch (e) {
      toastApiError(e, "Could not restore the original content.", {
        context: { deck_id: deckId, slide_num: slideNum, extra_id: extraId || null, action: "premium_rewrite_restore" },
      });
    } finally { setRestoring(false); }
  };

  const keepNew = () => {
    setDiffPreview(null);
    toast.success("Kept the rewrite. Your slide is updated.");
    setTimeout(() => setOpen(false), 400);
  };

  /** Precompute word-level diff segments once per (oldContent, newContent)
   *  pair. Cheap enough for slide content that we could recompute on every
   *  render, but memoising means toggling view mode is instant. */
  const diffSegments = useMemo(() => {
    if (!diffPreview) return null;
    return diffWords(diffPreview.oldContent || "", diffPreview.newContent || "");
  }, [diffPreview]);

  const revert = async () => {
    setLoading(true);
    try {
      await api.post(`/pitch/decks/${deckId}/slide/${slideNum}/reset-design${extraQs}`);
      // Dispatch an explicit clear — pass empty-string keys for all prefs so
      // the listener overwrites the current layout instead of merging (which
      // would leave the old layout in place).
      window.dispatchEvent(new CustomEvent("clinq:design-prefs-saved", {
        detail: { deckId, slideNum, extraId, prefs: { layout: "", infographic: "", chart: "", imagery: "", color: "" } },
      }));
      setSavedPrefs({});
      setPick(null);
      toast.success("Reverted to the original slide design — no credits used");
      setTimeout(() => setOpen(false), 700);
    } catch (e) {
      const detail = extractErrorMessage(e, "Could not revert design");
      toast.error(typeof detail === "string" ? detail : "Could not revert design");
    } finally { setLoading(false); }
  };

  const hasCustomLayout = !!(savedPrefs && savedPrefs.layout);
  const targetLabel = extraId ? "sub-slide" : "slide";
  const buttonLabel = extraId ? "Change sub-slide design · 2 cr" : "Change this slide's design · 2 cr";

  return (
    <>
      <button onClick={() => setOpen(true)}
        data-testid={extraId ? `change-subslide-design-btn-${extraId}` : "change-slide-design-btn"}
        className="mt-4 text-[12px] font-bold px-4 py-2.5 rounded-md inline-flex items-center gap-2 transition-colors"
        style={{
          backgroundColor: "rgba(245, 158, 11, 0.10)",
          color: C.orange,
          border: "1px solid rgba(245, 158, 11, 0.35)",
        }}
        title={`Pick from 8 pre-built ${targetLabel} designs. Costs 2 credits to apply.`}>
        <PaintBrushBroad size={14} weight="fill" />
        {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
          onClick={() => setOpen(false)}
          data-testid="change-slide-modal">
          <div className="w-full max-w-4xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"
            style={{ backgroundColor: C.panel, border: "1px solid rgba(123, 211, 206, 0.18)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="p-6 pb-4 flex items-start gap-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <PaintBrushBroad size={22} weight="fill" style={{ color: C.orange, marginTop: 2 }} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.orange }}>
                  {extraId ? `Slide ${slideNum} · Sub-slide design gallery` : `Slide ${slideNum} · Design gallery`}
                </div>
                <h3 className="font-extrabold text-xl tracking-tight mt-0.5" style={{ letterSpacing: "-0.02em" }}>
                  Pick a new design for &ldquo;{slideTitle}&rdquo;
                </h3>
                <div className="text-[12px] text-white/50 mt-1">
                  Applies the layout to both the live preview and the exported .pptx. <strong className="text-white/70">2 credits</strong> to commit.
                </div>
              </div>
              <button onClick={() => setOpen(false)}
                data-testid="change-slide-modal-close"
                className="text-white/40 hover:text-white/80 p-1">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {layoutOrder.map((id) => {
                  const L = LAYOUTS_BY_ID[id];
                  if (!L) return null;
                  const active = pick === L.id;
                  return (
                    <button key={L.id} type="button"
                      onClick={() => setPick(L.id)}
                      data-testid={`layout-option-${L.id.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
                      className="text-left rounded-lg overflow-hidden transition-all"
                      style={{
                        border: `2px solid ${active ? C.orange : "rgba(255,255,255,0.08)"}`,
                        backgroundColor: active ? "rgba(245, 158, 11, 0.08)" : "rgba(255,255,255,0.02)",
                        transform: active ? "translateY(-1px)" : "none",
                        boxShadow: active ? "0 4px 16px rgba(245,158,11,0.18)" : "none",
                      }}>
                      <div className="aspect-video overflow-hidden" style={{ backgroundColor: "#fff" }}>
                        <L.Thumb />
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between gap-1">
                          <div className="text-[13px] font-bold tracking-tight text-white truncate" style={{ letterSpacing: "-0.01em" }}>
                            {L.title}
                          </div>
                          {active && <CheckCircle size={14} weight="fill" style={{ color: C.orange }} />}
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] mt-0.5" style={{ color: C.tealSoft }}>
                          {L.tag}
                        </div>
                        <div className="text-[11px] text-white/55 mt-1.5 leading-snug">
                          {L.desc}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="p-4 flex items-center justify-between gap-3 flex-wrap"
              style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="flex items-center gap-2">
                <button onClick={() => setOpen(false)}
                  data-testid="change-slide-close-btn"
                  className="text-sm font-semibold px-4 py-2 rounded-md hover:bg-white/5 text-white/60">
                  Cancel
                </button>
                {hasCustomLayout && (
                  <button onClick={revert} disabled={loading}
                    data-testid="change-slide-revert-btn"
                    className="text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                    style={{ color: C.tealSoft, border: "1px solid rgba(123, 211, 206, 0.28)", backgroundColor: "rgba(10, 139, 133, 0.06)" }}
                    title="Clear the custom design and go back to this slide's default template. Free.">
                    <ArrowCounterClockwise size={13} weight="bold" />
                    {loading ? "Reverting…" : "Revert to original · Free"}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={apply} disabled={loading || !pick || done}
                data-testid="change-slide-apply-btn"
                className="text-sm font-bold px-5 py-2.5 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                style={{ backgroundColor: done ? C.green : C.orange, color: "#1a1206" }}>
                {done
                  ? (<><CheckCircle size={13} weight="fill" /> Applied</>)
                  : (<><Sparkle size={13} weight="fill" />
                      {loading ? "Applying…" : `Apply new design · 2 cr`}
                      {!loading && <ArrowRight size={13} weight="bold" />}
                    </>)}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-photo confirmation. Fires ONLY when a photo layout is applied
          to a slide with no uploaded photo — offers to generate one via
          Nano Banana for 3 credits. The founder can skip and upload their
          own photo in the Photos section instead. If they later upload,
          any auto-generated photo is replaced (backend enforces this). */}
      {autoPhotoPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.82)" }}
          data-testid="auto-photo-prompt">
          <div className="w-full max-w-md rounded-2xl overflow-hidden"
            style={{ backgroundColor: C.panel, border: "1px solid rgba(123, 211, 206, 0.22)" }}>
            <div className="p-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.orange }}>
                Photo layout applied
              </div>
              <h3 className="font-extrabold text-lg tracking-tight mt-1" style={{ letterSpacing: "-0.02em" }}>
                Generate a matching photo?
              </h3>
              <div className="text-[13px] text-white/70 mt-2 leading-relaxed">
                &ldquo;{slideTitle}&rdquo; has no photo yet. Our AI can create one that fits your slide&apos;s content — professional editorial style, 16:9 landscape.
              </div>
              <div className="text-[11px] text-white/45 mt-3 leading-relaxed">
                <strong className="text-white/70">3 credits.</strong> You can upload your own photo in the Photos section anytime — it will replace the AI-generated one.
              </div>
            </div>
            <div className="p-4 flex items-center gap-2 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.35)" }}>
              <button onClick={skipAutoPhoto}
                disabled={autoPhotoBusy}
                data-testid="auto-photo-skip-btn"
                className="text-sm font-semibold px-4 py-2 rounded-md text-white/70 hover:text-white hover:bg-white/5 disabled:opacity-60">
                I&apos;ll upload my own
              </button>
              <button onClick={generateAutoPhoto}
                disabled={autoPhotoBusy}
                data-testid="auto-photo-generate-btn"
                className="text-sm font-bold px-5 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                style={{ backgroundColor: C.orange, color: "#1a1206" }}>
                <Sparkle size={13} weight="fill" />
                {autoPhotoBusy ? "Generating…" : "Generate · 3 cr"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Diff preview — appears after a successful premium rewrite. Shows the
          founder OLD vs NEW content side-by-side so they can inspect the AI's
          output before committing to it. "Restore original" reverts via PUT
          (no refund — the founder already got the preview value). */}
      {diffPreview && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.84)" }}
          data-testid="premium-rewrite-diff-modal">
          <div className="w-full max-w-5xl rounded-2xl overflow-hidden flex flex-col"
            style={{ backgroundColor: C.panel, border: "1px solid rgba(123, 211, 206, 0.22)", maxHeight: "88vh" }}>
            <div className="p-5 shrink-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.tealSoft }}>
                    Premium AI rewrite · Claude Opus 4.5
                  </div>
                  <h3 className="font-extrabold text-xl tracking-tight mt-1" style={{ letterSpacing: "-0.02em" }}>
                    Review the rewrite
                  </h3>
                  <div className="text-[12px] text-white/60 mt-1.5 leading-relaxed">
                    Your slide has been updated to the new content on the right.
                    Compare and pick which version to keep.
                    {diffPreview.layout && (
                      <> Layout: <span className="text-white/80 font-semibold">{diffPreview.layout.split("(")[0].trim()}</span>.</>
                    )}
                  </div>
                </div>
                <button onClick={() => setDiffPreview(null)}
                  data-testid="premium-rewrite-diff-close"
                  className="w-8 h-8 grid place-items-center rounded-md text-white/50 hover:text-white hover:bg-white/5 shrink-0"
                  aria-label="Close">
                  <X size={16} weight="bold" />
                </button>
              </div>
              {/* View-mode toggle — inline word diff (default) or classic side-by-side. */}
              <div className="mt-4 inline-flex rounded-md p-0.5"
                style={{ backgroundColor: "rgba(0,0,0,0.32)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <button
                  data-testid="premium-rewrite-diff-view-inline"
                  onClick={() => setDiffView("inline")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-bold uppercase tracking-[0.14em] transition-colors"
                  style={{
                    backgroundColor: diffView === "inline" ? "rgba(123, 211, 206, 0.14)" : "transparent",
                    color: diffView === "inline" ? C.tealSoft : "rgba(255,255,255,0.55)",
                  }}>
                  <TextT size={13} weight="bold" /> Word diff
                </button>
                <button
                  data-testid="premium-rewrite-diff-view-sxs"
                  onClick={() => setDiffView("sxs")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-bold uppercase tracking-[0.14em] transition-colors"
                  style={{
                    backgroundColor: diffView === "sxs" ? "rgba(123, 211, 206, 0.14)" : "transparent",
                    color: diffView === "sxs" ? C.tealSoft : "rgba(255,255,255,0.55)",
                  }}>
                  <Columns size={13} weight="bold" /> Side-by-side
                </button>
              </div>
            </div>
            {diffView === "inline" ? (
              /* Inline word-level diff — single pane showing the OLD text with
                 strikethrough red for removed words and inline green highlight
                 for added words. Reading like this ("here is what changed")
                 is much faster than eyeballing two 200-word panes. */
              <div className="flex-1 overflow-hidden flex flex-col" style={{ minHeight: 260 }}>
                <div className="px-5 py-3 shrink-0 flex items-center gap-4"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", backgroundColor: "rgba(0,0,0,0.24)" }}>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#EF4444" }} />
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/70">Removed</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#22C55E" }} />
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/70">Added</span>
                  </div>
                  <span className="text-[10.5px] text-white/40 ml-auto">
                    {diffSegments
                      ? `${diffSegments.filter((s) => s.type === "add").length} adds · ${diffSegments.filter((s) => s.type === "remove").length} removes`
                      : ""}
                  </span>
                </div>
                <pre
                  data-testid="premium-rewrite-diff-inline"
                  className="flex-1 overflow-auto px-5 py-4 text-[12.5px] leading-relaxed whitespace-pre-wrap font-mono"
                  style={{ color: "rgba(255,255,255,0.86)" }}>
                  {(diffSegments || []).map((seg, i) => {
                    if (seg.type === "equal") return <span key={i}>{seg.text}</span>;
                    if (seg.type === "add") return (
                      <span key={i}
                        data-testid="diff-add-seg"
                        style={{
                          backgroundColor: "rgba(34, 197, 94, 0.22)",
                          color: "#BBF7D0",
                          borderRadius: 3,
                          padding: "0 2px",
                        }}>{seg.text}</span>
                    );
                    return (
                      <span key={i}
                        data-testid="diff-remove-seg"
                        style={{
                          backgroundColor: "rgba(239, 68, 68, 0.22)",
                          color: "#FECACA",
                          textDecoration: "line-through",
                          textDecorationColor: "rgba(239, 68, 68, 0.75)",
                          borderRadius: 3,
                          padding: "0 2px",
                        }}>{seg.text}</span>
                    );
                  })}
                  {(!diffSegments || diffSegments.length === 0) && (
                    <span className="text-white/40 italic">(No changes)</span>
                  )}
                </pre>
              </div>
            ) : (
              <div className="flex-1 overflow-hidden grid grid-cols-2 gap-0" style={{ minHeight: 260 }}>
                <div className="flex flex-col overflow-hidden" style={{ borderRight: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="px-5 py-3 shrink-0 flex items-center gap-2"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", backgroundColor: "rgba(0,0,0,0.24)" }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#EF4444" }} />
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/70">Original</span>
                  </div>
                  <pre
                    data-testid="premium-rewrite-diff-old"
                    className="flex-1 overflow-auto px-5 py-4 text-[12.5px] leading-relaxed whitespace-pre-wrap font-mono"
                    style={{ backgroundColor: "rgba(239, 68, 68, 0.04)", color: "rgba(255,255,255,0.82)" }}
                  >
                    {(diffToOldSegments(diffSegments || [])).map((seg, i) =>
                      seg.type === "remove" ? (
                        <span key={i}
                          style={{
                            backgroundColor: "rgba(239, 68, 68, 0.22)",
                            color: "#FECACA",
                            textDecoration: "line-through",
                            textDecorationColor: "rgba(239, 68, 68, 0.75)",
                            borderRadius: 3,
                            padding: "0 2px",
                          }}>{seg.text}</span>
                      ) : (<span key={i}>{seg.text}</span>)
                    )}
                    {(!diffPreview.oldContent) && <span className="text-white/40 italic">(empty)</span>}
                  </pre>
                </div>
                <div className="flex flex-col overflow-hidden">
                  <div className="px-5 py-3 shrink-0 flex items-center gap-2"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", backgroundColor: "rgba(0,0,0,0.24)" }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#22C55E" }} />
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/70">New · Opus 4.5</span>
                  </div>
                  <pre
                    data-testid="premium-rewrite-diff-new"
                    className="flex-1 overflow-auto px-5 py-4 text-[12.5px] leading-relaxed whitespace-pre-wrap font-mono"
                    style={{ backgroundColor: "rgba(34, 197, 94, 0.06)", color: "#FFFFFF" }}
                  >
                    {(diffToNewSegments(diffSegments || [])).map((seg, i) =>
                      seg.type === "add" ? (
                        <span key={i}
                          style={{
                            backgroundColor: "rgba(34, 197, 94, 0.22)",
                            color: "#BBF7D0",
                            borderRadius: 3,
                            padding: "0 2px",
                          }}>{seg.text}</span>
                      ) : (<span key={i}>{seg.text}</span>)
                    )}
                    {(!diffPreview.newContent) && <span className="text-white/40 italic">(empty)</span>}
                  </pre>
                </div>
              </div>
            )}
            <div className="p-4 shrink-0 flex items-center justify-between gap-3 flex-wrap"
              style={{ borderTop: "1px solid rgba(255,255,255,0.06)", backgroundColor: "rgba(0,0,0,0.24)" }}>
              <div className="text-[11px] text-white/45 max-w-md leading-snug">
                Reverting keeps your 3 credits used — you can still ask for another rewrite later.
              </div>
              <div className="flex items-center gap-2">
                <button onClick={restoreOriginal}
                  disabled={restoring}
                  data-testid="premium-rewrite-restore-btn"
                  className="text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                  style={{
                    color: "#FFB4B4",
                    backgroundColor: "rgba(239, 68, 68, 0.10)",
                    border: "1px solid rgba(239, 68, 68, 0.35)",
                  }}>
                  <ArrowCounterClockwise size={13} weight="bold" />
                  {restoring ? "Restoring…" : "Restore original"}
                </button>
                <button onClick={keepNew}
                  disabled={restoring}
                  data-testid="premium-rewrite-keep-btn"
                  className="text-sm font-bold px-5 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                  style={{ backgroundColor: C.green, color: "#052015" }}>
                  <CheckCircle size={13} weight="fill" />
                  Keep the rewrite
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
